import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import { CompareType } from 'webapp/enums/enums';
import { getIndicatorClass } from 'webapp/utils/number';

import type { DataSelect } from 'webapp/models-custom';
import type DsDataPointCompare from 'webapp/models-custom/ds-data/point/compare';
import type DsDataPointTopLevel from 'webapp/models-custom/ds-data/point/top';

export const DEFAULT_SKELETON_COLUMNS = 4;

export interface HeaderTimestamp {
	label: string;
	ts: number;
}

interface AdditionalValue {
	dataSelect: DataSelect | null;
}

interface IndicatorConfig {
	dataSelect: DataSelect | null;
	changeInvert?: boolean;
}

interface Args {
	dataSelect: DataSelect | null;
	outputIndex?: number;
	additionalValues?: AdditionalValue[];
	indicatorConfig?: IndicatorConfig;
	cellClass?: string;
	isLoading?: boolean;
	isError?: boolean;
	skeletonColumns?: number;
	summaryColumnOnLeft?: boolean;
	timelinePeriods: HeaderTimestamp[];
}

interface MetricDetailsTableRowComponentSignature {
	Args: Args;
	Blocks: {
		header: [];
		summary: [];
	};
}

export default class MetricDetailsTableRowComponent extends Component<MetricDetailsTableRowComponentSignature> {
	get outputIndex(): number {
		return this.args.outputIndex ?? 0;
	}

	// --- Point maps ---

	@cached
	get primaryPointsMap(): Map<number, DsDataPointTopLevel> {
		return this.createPointsMap(this.args.dataSelect, this.outputIndex);
	}

	@cached
	get additionalPointsMaps(): Map<number, DsDataPointTopLevel>[] {
		return (this.args.additionalValues || []).map((av) =>
			this.createPointsMap(av.dataSelect, this.outputIndex),
		);
	}

	@cached
	get indicatorPointsMap(): Map<number, DsDataPointTopLevel> | null {
		if (!this.args.indicatorConfig) return null;
		return this.createPointsMap(this.args.indicatorConfig.dataSelect, this.outputIndex);
	}

	@cached
	get additionalTimestampMappings(): Map<number, number>[] {
		return (this.args.additionalValues || []).map((av) =>
			this.createTimestampMapping(av.dataSelect),
		);
	}

	@cached
	get indicatorTimestampMapping(): Map<number, number> {
		if (!this.args.indicatorConfig) return new Map();
		return this.createTimestampMapping(this.args.indicatorConfig.dataSelect);
	}

	// --- Value retrieval ---

	getPrimaryValue = (timestamp: number): string => {
		const point = this.primaryPointsMap.get(timestamp);
		return point ? point.valueStr : '-';
	};

	getAdditionalValue = (mapIndex: number, timestamp: number): string => {
		const map = this.additionalPointsMaps[mapIndex];
		if (!map) return '-';
		const tsMapping = this.additionalTimestampMappings[mapIndex] || new Map<number, number>();
		const point = this.getPointFromMap(map, tsMapping, timestamp);
		return point ? point.valueStr : '-';
	};

	private getNonGoalComparePoints(point: DsDataPointTopLevel | undefined): DsDataPointCompare[] {
		if (!point?.comparePoints?.length) return [];
		return point.comparePoints.filter((cp) => cp.compareSettings?.type !== CompareType.GOAL);
	}

	@cached
	get compareLineCount(): number {
		let max = 0;
		for (const point of this.primaryPointsMap.values()) {
			const nonGoalCompares = this.getNonGoalComparePoints(point);
			if (nonGoalCompares.length > max) {
				max = nonGoalCompares.length;
			}
		}
		return max;
	}

	getCompareValues = (timestamp: number): string[] => {
		const point = this.primaryPointsMap.get(timestamp);
		const nonGoalCompares = this.getNonGoalComparePoints(point);
		const values: string[] = [];
		for (let i = 0; i < this.compareLineCount; i++) {
			const cp = nonGoalCompares[i];
			values.push(cp?.valueStr || '-');
		}
		return values;
	};

	getIndicatorClass = (timestamp: number): string => {
		if (!this.args.indicatorConfig || !this.indicatorPointsMap) return '';

		const primaryPoint = this.primaryPointsMap.get(timestamp);
		const referencePoint = this.getPointFromMap(
			this.indicatorPointsMap,
			this.indicatorTimestampMapping,
			timestamp,
		);

		if (
			!primaryPoint?.hasValue ||
			primaryPoint.incomplete ||
			!referencePoint?.hasValue ||
			primaryPoint.value === null ||
			referencePoint.value === null
		) {
			return '';
		}

		const change = primaryPoint.value - referencePoint.value;
		return getIndicatorClass(change, this.args.indicatorConfig.changeInvert);
	};

	// --- Loading/error helpers ---

	get cellCount(): number {
		return (
			this.args.timelinePeriods.length ||
			(this.args.skeletonColumns ?? DEFAULT_SKELETON_COLUMNS)
		);
	}

	get cellIndices(): number[] {
		return Array.from({ length: this.cellCount }, (_, i) => i);
	}

	getTsForIndex = (index: number): number => {
		return this.args.timelinePeriods[index]?.ts ?? 0;
	};

	// --- Private helpers ---

	private createPointsMap(
		dataSelect: DataSelect | null,
		outputIndex: number,
	): Map<number, DsDataPointTopLevel> {
		const pointsMap = new Map<number, DsDataPointTopLevel>();
		const output = dataSelect?.outputs?.[outputIndex];

		if (output?.points) {
			for (const point of output.points) {
				if (point.hasValue) {
					pointsMap.set(point.ts, point);
				}
			}
		}

		return pointsMap;
	}

	private createTimestampMapping(dataSelect: DataSelect | null): Map<number, number> {
		const mappingMap = new Map<number, number>();
		const dataRoot = dataSelect?.dataRoot;

		if (!dataRoot?.granulationLabels) {
			return mappingMap;
		}

		this.args.timelinePeriods.forEach((period, index) => {
			const granulationLabel = dataRoot.granulationLabels[index];
			if (granulationLabel) {
				mappingMap.set(period.ts, granulationLabel.ts);
			}
		});

		return mappingMap;
	}

	private getPointFromMap(
		pointsMap: Map<number, DsDataPointTopLevel>,
		timestampMapping: Map<number, number>,
		timestamp: number,
	): DsDataPointTopLevel | undefined {
		let point = pointsMap.get(timestamp);
		if (!point) {
			const mappedTs = timestampMapping.get(timestamp);
			if (mappedTs !== undefined) {
				point = pointsMap.get(mappedTs);
			}
		}
		return point;
	}

	<template>
		<tr class={{if @isError "error-row"}}>
			{{! Left: label cell }}
			<td class="label-column {{@cellClass}}">
				{{yield to="header"}}
			</td>

			{{! Summary cell: when positioned on left, render after label }}
			{{#if @summaryColumnOnLeft}}
				<td class="summary-column {{@cellClass}}">
					{{#if @isLoading}}
						<div class="animated-gradient skeleton-items">
							<div class="line line-50"></div>
						</div>
					{{else if (has-block "summary")}}
						{{yield to="summary"}}
					{{else}}
						<span class="data-value primary">-</span>
					{{/if}}
				</td>
			{{/if}}

			{{! Middle: data cells }}
			{{#each this.cellIndices as |cellIndex|}}
				<td class="data-column {{if @isError 'error-cell'}} {{@cellClass}}">
					<div class="data-cell-content">
						{{#if @isError}}
							<span class="data-value">-</span>
							{{#each @additionalValues}}
								<span class="data-value">-</span>
							{{/each}}
						{{else if @isLoading}}
							<span class="skeleton skeleton-text-medium"></span>
							{{#each @additionalValues}}
								<span class="skeleton skeleton-text-medium"></span>
							{{/each}}
						{{else}}
							<span
								class="data-value primary
									{{this.getIndicatorClass (this.getTsForIndex cellIndex)}}"
							>
								{{this.getPrimaryValue (this.getTsForIndex cellIndex)}}
							</span>
							{{#each @additionalValues as |_av avIndex|}}
								<span class="data-value additional">
									{{this.getAdditionalValue
										avIndex
										(this.getTsForIndex cellIndex)
									}}
								</span>
							{{/each}}
							{{#each
								(this.getCompareValues (this.getTsForIndex cellIndex))
								as |compareValue|
							}}
								<span class="data-value compare">{{compareValue}}</span>
							{{/each}}
						{{/if}}
					</div>
				</td>
			{{/each}}

			{{! Right: summary cell (default end position) }}
			{{#unless @summaryColumnOnLeft}}
				<td class="summary-column {{@cellClass}}">
					{{#if @isLoading}}
						<div class="animated-gradient skeleton-items">
							<div class="line line-50"></div>
						</div>
					{{else if (has-block "summary")}}
						{{yield to="summary"}}
					{{else}}
						<span class="data-value primary">-</span>
					{{/if}}
				</td>
			{{/unless}}
		</tr>
	</template>
}
